import {
  PROGRAM_EDITORS,
  PROGRAM_STEP_ROUTES
} from '@/shared/programs/registry';
import { NAV_PAGES } from '@/shared/routes/pageRegistry';
import {
  APP_PREFIX,
  APP_ROUTES,
  LEGAL_ROUTES,
  LOGIN_ROUTES,
  PUBLIC_ROUTES
} from '@/shared/routes/paths';

/** Page context before the browser is available (prerender). See context.ts. */
export const UNKNOWN_PAGE = 'unknown';
/** Authenticated page that belongs to no gated feature. */
export const NO_FEATURE = 'none';

export interface PageContext {
  page_path: string;
  page_name: string;
  feature_key: string;
}

const escapeRe = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const prefixMatch = (path: string): RegExp =>
  new RegExp(`^${escapeRe(path)}(/|$)`);

// Routes with no sidebar entry. Checked after NAV_PAGES, so a path that also
// matches a nav prefix never reaches this list.
const FALLBACK_ROUTES: Array<{
  match: RegExp;
  name: string;
  featureKey?: string;
}> = [
  { match: prefixMatch(LOGIN_ROUTES.ROOT), name: 'Login' },
  {
    match: prefixMatch(PUBLIC_ROUTES.REQUEST_ACCESS),
    name: 'Prospect Sign In'
  },
  { match: prefixMatch(APP_ROUTES.PROFILE), name: 'User Settings' },
  { match: prefixMatch(APP_ROUTES.ORGANIZATION), name: 'My Organization' },
  { match: prefixMatch(APP_ROUTES.SETTINGS), name: 'Settings' },
  {
    match: prefixMatch(PROGRAM_EDITORS['cleanout-landing'].canonicalPath),
    name: 'Cleanout Landing Page',
    featureKey: PROGRAM_EDITORS['cleanout-landing'].feature
  },
  { match: prefixMatch(LEGAL_ROUTES.PRIVACY), name: 'Legal — Privacy' },
  {
    match: prefixMatch(LEGAL_ROUTES.TERMS_OF_USE),
    name: 'Legal — Terms of Use'
  },
  {
    match: prefixMatch(LEGAL_ROUTES.PRIVACY_CHOICES),
    name: 'Your Privacy Choices'
  },
  { match: prefixMatch(APP_ROUTES.NOT_FOUND), name: 'Not Found' }
];

// An editor step renders the editor in place, so it reports as the editor's
// own page rather than as the Programs list its URL is nested under. Lower-
// cased because route matching is case-insensitive.
const editorCanonicalPath = (pathname: string): string | undefined => {
  const path = pathname.toLowerCase().replace(/\/+$/, '');
  const route = PROGRAM_STEP_ROUTES.find(
    (r) => `${APP_PREFIX}/${r.path}` === path
  );
  return route?.step.surface === 'editor'
    ? PROGRAM_EDITORS[route.step.editor].canonicalPath
    : undefined;
};

export function resolvePageContext(pathname: string): PageContext {
  if (pathname === PUBLIC_ROUTES.LANDING) {
    return {
      page_path: pathname,
      page_name: 'RaaS Landing',
      feature_key: NO_FEATURE
    };
  }

  if (pathname === PUBLIC_ROUTES.CONTACT) {
    return {
      page_path: pathname,
      page_name: 'Contact Us',
      feature_key: NO_FEATURE
    };
  }

  if (pathname === APP_ROUTES.ROOT) {
    return { page_path: pathname, page_name: 'Home', feature_key: NO_FEATURE };
  }

  const lookupPath = editorCanonicalPath(pathname) ?? pathname;

  const navPage = NAV_PAGES.find(
    (item) => item.path === lookupPath || lookupPath.startsWith(`${item.path}/`)
  );
  if (navPage) {
    return {
      page_path: pathname,
      page_name: navPage.title,
      feature_key: navPage.featureKey
    };
  }

  const fallback = FALLBACK_ROUTES.find((entry) =>
    entry.match.test(lookupPath)
  );
  return {
    page_path: pathname,
    page_name: fallback ? fallback.name : 'Not Found',
    feature_key: fallback?.featureKey ?? NO_FEATURE
  };
}

/**
 * Read the page straight off `window.location` rather than off React state.
 *
 * posthog-js captures its automatic `$pageview` synchronously inside the
 * patched `history.pushState`, i.e. before React re-renders — so any snapshot
 * written from an effect is always one navigation behind. By the time that
 * capture runs `window.location` already holds the new URL, and `$pageleave`
 * only fires on unload, so location is authoritative in both cases.
 */
export function currentPageContext(): PageContext {
  if (typeof window === 'undefined') {
    return {
      page_path: UNKNOWN_PAGE,
      page_name: UNKNOWN_PAGE,
      feature_key: NO_FEATURE
    };
  }
  return resolvePageContext(window.location.pathname);
}
